import {
    GooglePartner,
    HubspotPartner,
    KlaviyoPartner,
    MetaPartner,
    MicrosoftPartner,
} from "../assets";
import {
    currentHolidayLong,
    currentHolidayShort,
    isHolidayFocus,
    mostRecentOfferCtaUrl,
} from "../models/variableCopywriting";

// ─── Nav columns ──────────────────────────────────────────────────────────────
const NAV_COLUMNS = [
    {
        heading: "Company",
        links: [
            { label: "Home", href: "/", internal: true },
            { label: "All Services", href: "/services", internal: true },
            { label: "Monthly Retainer", href: "/seo-retainer", internal: true },
            { label: "Win a Free Month", href: "/#free-seo", internal: true },
            { label: "Contact Us", href: "/contact-us/", internal: false },
            { label: "Schedule a Call", href: "/contact-us", internal: false },
        ],
    },
    {
        heading: "Services",
        links: [
            { label: "SEO Setup", href: "/services/setup", internal: false },
            { label: "On-Site SEO", href: "/services/onsite-seo", internal: false },
            { label: "Technical SEO", href: "/services/technical", internal: false },
            { label: "SEO Research", href: "/services/seo-research", internal: false },
            { label: "AI SEO / GEO", href: "/services/geo", internal: true },
            { label: "View All Services", href: "/services/home", internal: true },
        ],
    },
    {
        heading: "Account",
        links: [
            { label: "Log In",    href: "/auth/login",  internal: true },
            { label: "Sign Up",   href: "/auth/signup", internal: true },
            { label: "Dashboard", href: "/dashboard",   internal: true },
        ],
    },
    {
        heading: "Legal",
        links: [
            { label: "Privacy Policy", href: "/privacy-policy", internal: false },
            { label: "contact@herstoryseo.com", href: "mailto:contact@herstoryseo.com", internal: false },
        ],
    },
];

const PARTNERS = [
    { src: GooglePartner, alt: "Google Premier Partner 2025" },
    { src: MetaPartner, alt: "Meta Business Partner 2026" },
    { src: MicrosoftPartner, alt: "Microsoft Advertising Partner 2025" },
    { src: KlaviyoPartner, alt: "Klaviyo Master + Champion 2026" },
    { src: HubspotPartner, alt: "HubSpot Certified Partner 2026" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function FooterLink({ href, label, internal }) {
    return (
        <li>
            <a
                href={href}
                target={internal ? "_self" : "_blank"}
                rel={internal ? undefined : "noopener noreferrer"}
                className="text-sm text-stone-400 hover:text-amber-400 transition-colors duration-200 no-underline"
            >
                {label}
            </a>
        </li>
    );
}

function NavColumn({ heading, links }) {
    return (
        <div>
            <p className="font-serif text-base text-white mb-4">{heading}</p>
            <ul className="space-y-2.5">
                {links.map((link) => (
                    <FooterLink key={link.href + link.label} {...link} />
                ))}
            </ul>
        </div>
    );
}

// ─── Main Footer ──────────────────────────────────────────────────────────────
export default function FooterView() {
    return (
        <footer className="bg-stone-950 text-white">
            {/* Top promise bar */}
            <div className="border-b border-white/8">
                <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                        <span className="text-xs font-medium text-stone-300">HerStory Fiscal Promise</span>
                    </div>
                    <p className="text-xs text-stone-500 font-light text-center sm:text-right max-w-sm">
                        Not fully satisfied within 30 days? You'll receive a full refund. No questions asked.
                    </p>
                </div>
            </div>

            {/* Main grid */}
            <div className="max-w-6xl mx-auto px-6 pt-16 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-6 gap-12 lg:gap-8">

                    {/* Brand column — takes 2 of 5 cols */}
                    <div className="lg:col-span-2">
                        <a href="/" className="inline-block mb-6 no-underline">
                            <img
                                src="/media/horizontal-logo.webp"
                                alt="HerStory SEO: Home"
                                className="w-52 invert"
                            />
                        </a>
                        <p className="text-sm text-stone-400 font-light leading-relaxed max-w-xs mb-8">
                            SEO built for serious businesses. We turn organic search into your most reliable growth channel.
                        </p>

                        {/* Holiday CTA — conditionally shown */}
                        {isHolidayFocus && (
                            <a
                                href={`${mostRecentOfferCtaUrl}?source=footer`}
                                target="_self"
                                className="inline-flex items-center gap-2 text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 px-4 py-2.5 rounded-full no-underline hover:bg-amber-500/20 transition-colors duration-200 mb-8"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                                {currentHolidayShort} Application — Now Open
                            </a>
                        )}

                        {/* Partner badges */}
                        <div>
                            <p className="text-[10px] font-medium tracking-widest uppercase text-stone-600 mb-3">
                                Certified Partners
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                                {PARTNERS.map(({ src, alt }) => (
                                    <img
                                        key={alt}
                                        src={src}
                                        alt={alt}
                                        title={alt}
                                        className="h-7 w-auto opacity-50 hover:opacity-80 transition-opacity duration-200 grayscale"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Nav columns — each takes 1 of 5 cols */}
                    {NAV_COLUMNS.map((col) => (
                        <NavColumn key={col.heading} {...col} />
                    ))}
                </div>
            </div>

            {/* Divider */}
            <div className="max-w-6xl mx-auto px-6">
                <div className="border-t border-white/8" />
            </div>

            {/* Bottom bar */}
            <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-stone-600">
                    © {new Date().getFullYear()} HerStory SEO. All rights reserved since 2018.
                </p>
                <div className="flex items-center gap-4">
                    <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-stone-600 hover:text-stone-400 transition-colors no-underline"
                    >
                        Privacy Policy
                    </a>
                    <span className="text-stone-700 text-xs">·</span>
                    <a
                        href="/contact-us"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-stone-600 hover:text-stone-400 transition-colors no-underline"
                    >
                        Contact
                    </a>
                    <span className="text-stone-700 text-xs">·</span>
                    <a
                        href="/services"
                        target="_self"
                        className="text-xs text-stone-600 hover:text-stone-400 transition-colors no-underline"
                    >
                        Shop Services
                    </a>
                </div>
            </div>
        </footer>
    );
}